"use client";

import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";
import NavigationArrow from "@/assets/NavigationArrow";

type Props = PropsWithChildren<{
  options?: Parameters<typeof useEmblaCarousel>[0];
  autoplayDelay?: number;
  autoplay?: boolean; // 👈 new prop
  navigationButtonsType?: 1 | 2;
  navigationButtonClassName?: string;
  leftNavigationIcon?: React.ReactNode;
  rightNavigationIcon?: React.ReactNode;
  onSlideChange?: (index: number) => void;
}>;

export default function Carousel1({
  children,
  options,
  autoplayDelay = 5000,
  navigationButtonsType = 1,
  navigationButtonClassName,
  autoplay = true, // default enabled
  leftNavigationIcon,
  rightNavigationIcon,
  onSlideChange,
}: Props) {
  const autoplayPlugin = Autoplay({
    delay: Number(autoplayDelay) || 5000,
    stopOnInteraction: false,
  });

  // Only include plugin if autoplay is enabled
  const plugins = autoplay ? [autoplayPlugin] : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    setSelectedIndex(newIndex);
    onSlideChange?.(newIndex);
  }, [emblaApi, onSlideChange]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="w-full relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              className="flex-[0_0_100%] min-w-0 flex justify-center items-center"
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {scrollSnaps.length > 1 && navigationButtonsType === 1 && (
        <div
          className={cn(
            "flex justify-center gap-2 mt-4",
            navigationButtonClassName
          )}
        >
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              className={cn(
                "h-3 rounded-full transition-all cursor-pointer",
                i === selectedIndex
                  ? "w-[26px] bg-secondary-main scale-110"
                  : "w-3 bg-gray-300"
              )}
            />
          ))}
        </div>
      )}

      {navigationButtonsType === 2 && (
        <div className="flex items-center justify-center w-full">
          <div
            className={cn(
              "flex items-center justify-between mt-4",
              navigationButtonClassName
            )}
          >
            <div
              className={cn("cursor-pointer", {
                "opacity-50": selectedIndex === 0,
              })}
              onClick={() => emblaApi?.scrollPrev()}
            >
              {leftNavigationIcon}
            </div>
            <div
              className={cn("cursor-pointer", {
                "opacity-50": selectedIndex === scrollSnaps.length - 1,
              })}
              onClick={() => {
                emblaApi?.scrollNext();
              }}
            >
              {rightNavigationIcon}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
